/**
 * The reviewer's issues on a draft. Each card says what's wrong and where —
 * its quote, or its page when no paragraph is named — and what a person can
 * do, in words that say what happens: fix it yourself (its paragraph opens
 * for editing; saving the draft calls it fixed), make a layout fault's
 * one-step fix (on save), keep it as it is, dismiss it as the reviewer's
 * mistake, or call it fixed on their own word (it was fixed some other way:
 * decision 18) — keys 1, 2, 3 and 4 on the selected card — or hand it to
 * the Assistant, which proposes the edit. An issue with a page shows on the
 * page, as the client gets it. An open validator error stops the draft's
 * release until one of those; the reviewer's judgement goes with it to QA.
 *
 * Beside the draft on a wide screen the cards sit in a margin, level with
 * their paragraphs (`ReviewMargin`); on a narrow one they're a list
 * (`ReviewIssues`), open first, errors before warnings, closed ones folded.
 */

import { useState, type CSSProperties, type ReactNode, type Ref } from 'react';

import { Anchor, Badge, Button, Card, Group, Stack, Text } from '@mantine/core';

import type { ReviewIssue, ReviewIssueStatus } from '@/types/workbench';
import { toError } from '@/utils/errors';
import { words } from './format';
import { FIX_LABELS, issueRank, paraKey } from './marginLayout';
import { Kbd } from './panes/common';
import classes from './DraftView.module.css';

export type Resolution = Exclude<ReviewIssueStatus, 'open'>;

export interface IssueCardProps {
  issue: ReviewIssue;
  /** Selected: its paragraph is outlined in the draft. */
  selected: boolean;
  /** Pick it — the draft shows its paragraph. */
  onSelect: (issue: ReviewIssue) => void;
  /** A person has changed its paragraph in this draft: it can be called fixed. */
  changed: boolean;
  /** A change to its paragraph waits to be saved. */
  pendingEdit: boolean;
  /** Its one-step layout fix can be made here (a live draft), and whether
   * it waits to be saved. */
  canFix: boolean;
  fixPending: boolean;
  onToggleFix: (issue: ReviewIssue) => void;
  /** Open its paragraph to fix it by hand; absent where that can't be done
   * here (a partner's return, a draft not live, an issue on no paragraph). */
  onFixYourself?: (issue: ReviewIssue) => void;
  /** Hand it to the Assistant, which proposes the edit. */
  onFixWithAssistant?: (issue: ReviewIssue) => void;
  /** Close it as a person decides. */
  onResolve: (issue: ReviewIssue, status: Resolution) => Promise<void>;
  /** Show it on its page, as the client gets it. */
  onSeePage?: (issue: ReviewIssue) => void;
  /** The keys act on the selected card: say which. */
  keys?: boolean;
  cardRef?: Ref<HTMLDivElement>;
  style?: CSSProperties;
}

export function IssueCard({
  issue,
  selected,
  onSelect,
  changed,
  pendingEdit,
  canFix,
  fixPending,
  onToggleFix,
  onFixYourself,
  onFixWithAssistant,
  onResolve,
  onSeePage,
  keys = false,
  cardRef,
  style,
}: Readonly<IssueCardProps>) {
  const [busy, setBusy] = useState<Resolution | null>(null);
  const [error, setError] = useState<string | null>(null);
  const open = issue.status === 'open';
  const placed = paraKey(issue) !== null;
  const hint = (key: string) => (keys && selected ? <Kbd>{key}</Kbd> : null);

  const resolve = async (status: Resolution) => {
    setBusy(status);
    setError(null);
    try {
      await onResolve(issue, status);
    } catch (err) {
      setError(toError(err, 'Not recorded').message);
    } finally {
      setBusy(null);
    }
  };

  const action = (status: Resolution, label: string, key?: string) => (
    <Button
      key={status}
      size="compact-xs"
      variant={status === 'dismissed' ? 'subtle' : 'default'}
      color={status === 'dismissed' ? 'gray' : undefined}
      loading={busy === status}
      disabled={busy !== null || fixPending}
      onClick={(event) => {
        event.stopPropagation();
        void resolve(status);
      }}
      data-testid={`review-issue-${status}`}
    >
      {label}
      {key ? hint(key) : null}
    </Button>
  );

  return (
    <Card
      ref={cardRef}
      withBorder
      padding="xs"
      radius="sm"
      style={style}
      className={selected ? classes.cardSelected : undefined}
      onClick={() => onSelect(issue)}
      data-testid={`review-issue-${issue.id}`}
      data-selected={selected || undefined}
    >
      <Stack gap={4}>
        <Group gap={6}>
          <Badge
            size="xs"
            variant="light"
            color={!open ? 'gray' : issue.severity === 'error' ? 'red' : 'orange'}
          >
            {open ? issue.severity : words(issue.status)}
          </Badge>
          <Text size="xs" c="dimmed">
            {words(issue.rule)}
            {issue.page !== null ? ` · page ${issue.page}` : ''}
          </Text>
        </Group>
        <Text size="xs">{issue.message}</Text>
        {issue.quote && placed ? (
          <Anchor
            component="button"
            type="button"
            size="xs"
            fs="italic"
            c="dimmed"
            onClick={(event) => {
              event.stopPropagation();
              onSelect(issue);
            }}
            style={{ textAlign: 'left' }}
          >
            “{issue.quote}”
          </Anchor>
        ) : placed ? (
          <Anchor
            component="button"
            type="button"
            size="xs"
            c="dimmed"
            onClick={(event) => {
              event.stopPropagation();
              onSelect(issue);
            }}
            style={{ textAlign: 'left' }}
          >
            Show the paragraph
          </Anchor>
        ) : (
          <Text size="xs" c="dimmed">
            Not tied to a paragraph
          </Text>
        )}
        {open ? (
          <>
            <Group gap={4} wrap="wrap">
              {canFix && issue.layoutFix ? (
                <Button
                  size="compact-xs"
                  variant={fixPending ? 'light' : 'filled'}
                  onClick={(event) => {
                    event.stopPropagation();
                    onToggleFix(issue);
                  }}
                  title="Made in the draft by code when you save; nothing else moves"
                  data-testid="review-issue-fix"
                >
                  {fixPending ? 'Undo the fix' : FIX_LABELS[issue.layoutFix]}
                  {hint('1')}
                </Button>
              ) : onFixYourself && !changed ? (
                <Button
                  size="compact-xs"
                  variant="filled"
                  onClick={(event) => {
                    event.stopPropagation();
                    onFixYourself(issue);
                  }}
                  title="Edit the paragraph; saving the draft calls it fixed"
                  data-testid="review-issue-fix-yourself"
                >
                  Fix it myself
                  {hint('1')}
                </Button>
              ) : null}
              {onFixWithAssistant ? (
                <Button
                  size="compact-xs"
                  variant="light"
                  onClick={(event) => {
                    event.stopPropagation();
                    onFixWithAssistant(issue);
                  }}
                  title="The Assistant proposes the edit; you apply it"
                  data-testid="review-issue-fix-assistant"
                >
                  Ask the Assistant to fix it
                </Button>
              ) : null}
              {action('accepted', 'Keep it as it is', '2')}
              {action('dismissed', 'Dismiss — the reviewer is wrong', '3')}
              {action('fixed', changed ? 'Fixed' : 'Fixed (my word)', '4')}
              {onSeePage && issue.page !== null ? (
                <Button
                  size="compact-xs"
                  variant="light"
                  onClick={(event) => {
                    event.stopPropagation();
                    onSeePage(issue);
                  }}
                  data-testid="review-issue-see-page"
                >
                  See it on the page
                </Button>
              ) : null}
            </Group>
            {fixPending ? (
              <Text size="xs" c="dimmed">
                Made when you save
              </Text>
            ) : null}
            {pendingEdit && !changed ? (
              <Text size="xs" c="dimmed">
                Save the draft (⌘↵) to call it fixed.
              </Text>
            ) : null}
          </>
        ) : issue.resolutionNote ? (
          <Text size="xs" c="dimmed">
            {issue.resolutionNote}
          </Text>
        ) : null}
        {error ? (
          <Text size="xs" c="red">
            {error}
          </Text>
        ) : null}
      </Stack>
    </Card>
  );
}

interface ReviewIssuesProps {
  issues: ReviewIssue[];
  renderIssue: (issue: ReviewIssue) => ReactNode;
}

/** The issues as a list, beside the draft on a narrow screen. */
export function ReviewIssues({ issues, renderIssue }: Readonly<ReviewIssuesProps>) {
  const [showClosed, setShowClosed] = useState(false);
  const sorted = [...issues].sort((a, b) => issueRank(a) - issueRank(b));
  const open = sorted.filter((i) => i.status === 'open');
  const closed = sorted.filter((i) => i.status !== 'open');
  return (
    <Card withBorder padding="sm" radius="md" data-testid="review-issues">
      <Stack gap="xs">
        <Text size="xs" fw={700} tt="uppercase" c="dimmed" style={{ letterSpacing: '0.06em' }}>
          Review · {open.length} open
        </Text>
        {issues.length === 0 ? (
          <Text size="xs" c="dimmed">
            The reviewer hasn&apos;t raised anything on this draft.
          </Text>
        ) : null}
        {open.map(renderIssue)}
        <ClosedToggle count={closed.length} shown={showClosed} onToggle={setShowClosed} />
        {showClosed ? closed.map(renderIssue) : null}
      </Stack>
    </Card>
  );
}

export function ClosedToggle({
  count,
  shown,
  onToggle,
}: Readonly<{ count: number; shown: boolean; onToggle: (shown: boolean) => void }>) {
  if (count === 0) {
    return null;
  }
  return (
    <Anchor
      component="button"
      type="button"
      size="xs"
      c="dimmed"
      onClick={() => onToggle(!shown)}
      style={{ alignSelf: 'flex-start' }}
      data-testid="review-issues-closed"
    >
      {shown ? 'Hide the closed' : `${count} closed`}
    </Anchor>
  );
}
